import { z } from "zod";
import { ApiError, apiClient, apiErrorCode, firstValidationEntry } from "./api";
import { curationStateSchema } from "./curation-state";
import { llmCostLimitMessage, parseLlmCostLimitScope } from "./llm-cost-limits";
import { tagListSchema } from "./tags";

export const sourceFormatSchema = z.enum([
  "markdown",
  "text",
  "pdf",
  "docx",
  "pptx",
  "xlsx",
  "html",
  "image",
  "audio",
  "video",
  "s3_repo_doc",
  "api_operation",
]);
export type SourceFormat = z.infer<typeof sourceFormatSchema>;

export const sourceKindSchema = z.enum([
  "upload",
  "chat",
  "url",
  "github",
  "notion",
  "shortcut",
  "postman",
  "s3",
  "self",
  "jira",
  "feedback",
  "story_as_built",
]);
export type SourceKind = z.infer<typeof sourceKindSchema>;

export const syncStateSchema = z.enum(["synced", "pending", "processing", "failed", "sync_error"]);
export type SyncState = z.infer<typeof syncStateSchema>;

export const sourceListItemSchema = z.object({
  id: z.guid(),
  name: z.string(),
  source: sourceKindSchema,
  source_format: sourceFormatSchema.nullable(),
  sync_state: syncStateSchema,
  created_at: z.string(),
  last_synced_at: z.string().nullable(),
  last_sync_error: z.string().nullable(),
  external_url: z.string().nullable(),
  notion_workspace_id: z.guid().nullable().default(null),
  shortcut_workspace_id: z.guid().nullable().default(null),
  shortcut_public_id: z.number().int().nullable().default(null),
  description: z.string().nullable().default(null),
  archived: z.boolean().default(false),
  // Curation state (add-knowledge-curation-states), visible to every role.
  curation_state: curationStateSchema.default("active"),
  curation_protected: z.boolean().default(false),
  tags: tagListSchema.default([]),
  submitted_by: z.guid().nullable().default(null),
  submitted_by_email: z.string().nullable().default(null),
  submitted_by_full_name: z.string().nullable().default(null),
  validated_by: z.guid().nullable().default(null),
  validated_by_email: z.string().nullable().default(null),
  validated_by_full_name: z.string().nullable().default(null),
  // Notion database-row grouping (group-notion-database-rows-in-sources):
  // set only for a row whose Notion parent is a database (data source).
  // Null for ordinary pages and every non-Notion source.
  notion_database_id: z.string().nullable().default(null),
  notion_database_title: z.string().nullable().default(null),
});
export type SourceListItem = z.infer<typeof sourceListItemSchema>;

export const sourceListPageSchema = z.object({
  items: z.array(sourceListItemSchema),
  total: z.number().int().nonnegative(),
});
export type SourceListPage = z.infer<typeof sourceListPageSchema>;

export const resyncResultSchema = z.object({
  item: sourceListItemSchema,
  changed: z.boolean(),
  deleted: z.boolean().default(false),
  deleted_reason: z.string().nullable().default(null),
  warnings: z.array(z.string()).default([]),
});
export type ResyncResult = z.infer<typeof resyncResultSchema>;

export type SourceTypeFilter =
  | "all"
  | "upload"
  | "url"
  | "notion"
  | "s3"
  | "shortcut"
  | "postman"
  | "self"
  | "chat"
  | "jira"
  | "feedback"
  | "story_as_built";

export interface ListSourcesParams {
  type?: SourceTypeFilter;
  q?: string | null;
  limit?: number;
  offset?: number;
  /**
   * Deep-link target — when provided AND visible to the caller AND
   * matching the active filters, the backend places the matching row
   * at index 0 of the first page. Silently ignored when the target is
   * not visible or when `offset > 0`.
   */
  focusId?: string | null;
  /** Resolve submitter/approver profiles for the visible Chat listing. */
  includeActorDetails?: boolean;
}

/** Backend cap on `GET /sources?limit=` — keep in sync with `sources.py`. */
export const SOURCES_LIST_PAGE_MAX = 100;

export async function listSources(params: ListSourcesParams = {}): Promise<SourceListPage> {
  return apiClient.get("/sources", sourceListPageSchema, {
    query: {
      type: params.type ?? "all",
      q: params.q ?? undefined,
      limit: params.limit ?? 20,
      offset: params.offset ?? 0,
      focus_id: params.focusId ?? undefined,
      include_actor_details: params.includeActorDetails ?? undefined,
    },
  });
}

/** Walks offset pages until every row matching the filter is loaded. */
export async function listAllSources(
  params: Omit<ListSourcesParams, "limit" | "offset"> = {},
): Promise<SourceListPage> {
  const first = await listSources({ ...params, limit: SOURCES_LIST_PAGE_MAX, offset: 0 });
  if (first.total <= first.items.length) return first;

  const items = [...first.items];
  let offset = SOURCES_LIST_PAGE_MAX;
  while (items.length < first.total) {
    const page = await listSources({
      ...params,
      limit: SOURCES_LIST_PAGE_MAX,
      offset,
    });
    if (page.items.length === 0) break;
    items.push(...page.items);
    offset += SOURCES_LIST_PAGE_MAX;
  }
  return { items, total: first.total };
}

// ---- Source detail ------------------------------------------------

export const knowledgeItemStatusSchema = z.enum(["pending", "approved", "rejected"]);
export type KnowledgeItemStatus = z.infer<typeof knowledgeItemStatusSchema>;

export const chunkSummarySchema = z.object({
  id: z.guid(),
  text: z.string(),
  ord: z.number().int().nonnegative(),
});
export type ChunkSummary = z.infer<typeof chunkSummarySchema>;

export const entitySummarySchema = z.object({
  name: z.string(),
  type: z.string(),
});
export type EntitySummary = z.infer<typeof entitySummarySchema>;

export const sourceFullItemSchema = z.object({
  id: z.guid(),
  title: z.string(),
  status: knowledgeItemStatusSchema,
  source_format: sourceFormatSchema.nullable(),
  source_type: sourceKindSchema,
  external_url: z.string().nullable(),
  created_at: z.string(),
  last_synced_at: z.string().nullable(),
  chunk_count: z.number().int().nonnegative(),
  sync_state: syncStateSchema,
  description: z.string().nullable().default(null),
  full_content: z.string().nullable().default(null),
  submitted_by: z.guid(),
  submitted_by_email: z.string().nullable().default(null),
  submitted_by_full_name: z.string().nullable().default(null),
  validated_by: z.guid().nullable().default(null),
  validated_by_email: z.string().nullable().default(null),
  validated_by_full_name: z.string().nullable().default(null),
  tags: tagListSchema.default([]),
  curation_state: curationStateSchema.default("active"),
  curation_protected: z.boolean().default(false),
  // True when `GET /sources/{id}/original` serves the file this upload was
  // built from. A backend that predates the field never offers the download.
  has_original: z.boolean().default(false),
});
export type SourceFullItem = z.infer<typeof sourceFullItemSchema>;

export const sourceFullResponseSchema = z.object({
  item: sourceFullItemSchema,
  chunks: z.array(chunkSummarySchema),
  entities: z.array(entitySummarySchema),
});
export type SourceFullResponse = z.infer<typeof sourceFullResponseSchema>;

export async function getSourceFull(id: string): Promise<SourceFullResponse> {
  return apiClient.get(`/sources/${id}/full`, sourceFullResponseSchema);
}

/** Cache key of one item's detail under the selected knowledge repository. */
export function sourceFullQueryKey(
  scopeKey: string,
  id: string,
): readonly [string, string, string] {
  return ["source-full", scopeKey, id];
}

/**
 * The name a `Content-Disposition` header gives its file: the RFC 6266
 * `filename*=UTF-8''…` form first (it carries the real name), then the ASCII
 * `filename="…"` fallback. `null` when the header names none.
 */
function fileNameFromContentDisposition(header: string | null): string | null {
  if (header === null) return null;
  const encoded = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(header)?.[1];
  if (encoded !== undefined) {
    try {
      return decodeURIComponent(encoded.trim());
    } catch {
      // A malformed escape: fall through to the ASCII form.
    }
  }
  const quoted = /filename\s*=\s*"([^"]*)"/i.exec(header)?.[1];
  if (quoted !== undefined && quoted.length > 0) return quoted;
  return null;
}

interface SourceOriginalDownload {
  blob: Blob;
  fileName: string;
}

/**
 * The stored original of an upload (`GET /sources/{id}/original`). The route
 * needs the bearer token, so a plain link cannot reach it: the caller saves
 * the returned blob. The file name comes from the response's
 * `Content-Disposition`, then `fallbackName`, then `original`. Failures throw
 * the API client's `ApiError` (a 404 is the same for every cause).
 */
export async function downloadSourceOriginal(
  id: string,
  fallbackName = "original",
): Promise<SourceOriginalDownload> {
  const { blob, contentDisposition } = await apiClient.getBlob(`/sources/${id}/original`);
  return { blob, fileName: fileNameFromContentDisposition(contentDisposition) ?? fallbackName };
}

// ---- Refine knowledge ---------------------------------------------

export const refineLlmResponseSchema = z.object({
  refined_text: z.string(),
});
export type RefineLlmResponse = z.infer<typeof refineLlmResponseSchema>;

export async function refineLlmSource(
  id: string,
  instructions: string,
): Promise<RefineLlmResponse> {
  return apiClient.post(`/sources/${id}/refine-llm`, refineLlmResponseSchema, {
    instructions,
  });
}

export async function refineCommitSource(id: string, newText: string): Promise<SourceListItem> {
  return apiClient.post(`/sources/${id}/refine-commit`, sourceListItemSchema, {
    new_text: newText,
  });
}

// ---- Reprocess stuck embeddings -----------------------------------

/** Re-enqueue `process_approval_task` for an item whose embedding
 * pipeline did not reach `synced`. Returns the updated listing
 * projection (with `sync_state='pending'`). Throws an `ApiError`
 * with `body.detail.code` set to `already_running` or
 * `already_synced` on a 409, or `queue_unavailable` on a 503 after
 * the backend persists the row as failed. */
export async function reprocessSource(id: string): Promise<SourceListItem> {
  return apiClient.post(`/sources/${id}/reprocess`, sourceListItemSchema, {});
}

export interface RegisterUrlPayload {
  url: string;
  name?: string | null;
  metadata?: string | null;
}

export async function registerUrlSource(payload: RegisterUrlPayload): Promise<SourceListItem> {
  return apiClient.post("/sources/url", sourceListItemSchema, {
    url: payload.url,
    name: payload.name ?? null,
    metadata: payload.metadata ?? null,
  });
}

export async function resyncSource(id: string): Promise<ResyncResult> {
  return apiClient.post(`/sources/${id}/resync`, resyncResultSchema, {});
}

export async function deleteSource(id: string): Promise<void> {
  // Reuses the existing admin-only DELETE; the UI gates the button by role.
  await apiClient.delete(`/admin/knowledge-items/${id}`, z.unknown());
}

const bulkFailureSchema = z.object({ id: z.string(), error: z.string() });

export const bulkResyncResultSchema = z.object({
  requested: z.number(),
  failed: z.array(bulkFailureSchema),
});
export type BulkResyncResult = z.infer<typeof bulkResyncResultSchema>;

export const bulkDeleteResultSchema = z.object({
  deleted: z.number(),
  failed: z.array(bulkFailureSchema),
});
export type BulkDeleteResult = z.infer<typeof bulkDeleteResultSchema>;

// Best-effort bulk re-sync. The backend re-enqueues each id and never
// aborts the batch on a single failure; `failed` carries the per-id errors.
export async function bulkResyncSources(ids: string[]): Promise<BulkResyncResult> {
  return apiClient.post("/sources/bulk-resync", bulkResyncResultSchema, { ids });
}

// Best-effort bulk delete (admin-only on the backend). `deleted` counts
// items actually removed; `failed` carries per-id errors.
export async function bulkDeleteSources(ids: string[]): Promise<BulkDeleteResult> {
  return apiClient.post("/admin/knowledge-items/bulk-delete", bulkDeleteResultSchema, { ids });
}

// ---- Notion registration ------------------------------------------

export const notionRegistrationStatusSchema = z.enum(["running", "succeeded", "capped", "failed"]);
export type NotionRegistrationStatus = z.infer<typeof notionRegistrationStatusSchema>;

export const notionTargetKindSchema = z.enum(["page", "database"]);
export type NotionTargetKind = z.infer<typeof notionTargetKindSchema>;

export const notionRegistrationAuditSchema = z.object({
  id: z.guid(),
  registration_id: z.guid(),
  workspace_id: z.guid(),
  target_kind: notionTargetKindSchema,
  target_id: z.string(),
  requested_recursive: z.boolean(),
  requested_cap: z.number().int(),
  pages_persisted: z.number().int().nonnegative(),
  pages_skipped_cap: z.number().int().nonnegative(),
  // Defaulted rather than required so a backend predating the counter
  // cannot break the progress panel mid-deploy; absent reads as "nothing
  // known to have been blocked".
  pages_blocked: z.number().int().nonnegative().default(0),
  images_processed: z.number().int().nonnegative(),
  images_skipped_cap: z.number().int().nonnegative(),
  started_at: z.string(),
  finished_at: z.string().nullable(),
  status: notionRegistrationStatusSchema,
  error: z.string().nullable(),
});
export type NotionRegistrationAudit = z.infer<typeof notionRegistrationAuditSchema>;

export const notionRegistrationResultSchema = z.object({
  registration_id: z.guid(),
  audit_url: z.string(),
});
export type NotionRegistrationResult = z.infer<typeof notionRegistrationResultSchema>;

/**
 * Field caps enforced by `NotionRegistrationRequest` on the backend. Kept
 * here so the form can clamp the inputs and the error mapper can quote the
 * same numbers the API validates against.
 */
export const NOTION_METADATA_MAX_CHARS = 2000;
export const NOTION_NAME_MAX_CHARS = 200;

export interface RegisterNotionPayload {
  workspace_id: string;
  target: string;
  recursive?: boolean;
  name?: string | null;
  metadata?: string | null;
}

export async function registerNotionSource(
  payload: RegisterNotionPayload,
): Promise<NotionRegistrationResult> {
  return apiClient.post("/sources/notion", notionRegistrationResultSchema, {
    workspace_id: payload.workspace_id,
    target: payload.target,
    recursive: payload.recursive ?? true,
    name: payload.name ?? null,
    metadata: payload.metadata ?? null,
  });
}

export async function getNotionRegistrationAudit(
  registrationId: string,
): Promise<NotionRegistrationAudit> {
  return apiClient.get(
    `/sources/notion/registrations/${registrationId}`,
    notionRegistrationAuditSchema,
  );
}

// ---- Notion picker (admin-only search) -----------------------------

export const notionPickerObjectSchema = z.enum(["page", "database"]);
export type NotionPickerObject = z.infer<typeof notionPickerObjectSchema>;

export const notionPickerIconSchema = z.object({
  type: z.enum(["emoji", "external", "file"]),
  value: z.string(),
});
export type NotionPickerIcon = z.infer<typeof notionPickerIconSchema>;

export const notionPickerHitSchema = z.object({
  id: z.string(),
  object: notionPickerObjectSchema,
  title: z.string(),
  icon: notionPickerIconSchema.nullable(),
  parent_breadcrumb: z.string(),
  url: z.string(),
  already_registered: z.boolean(),
});
export type NotionPickerHit = z.infer<typeof notionPickerHitSchema>;

export const notionPickerResponseSchema = z.object({
  results: z.array(notionPickerHitSchema),
  has_more: z.boolean(),
  next_cursor: z.string().nullable(),
});
export type NotionPickerResponse = z.infer<typeof notionPickerResponseSchema>;

export type NotionPickerFilter = "all" | "page" | "database";

export interface SearchNotionPagesParams {
  workspaceId: string;
  q?: string;
  type?: NotionPickerFilter;
  pageSize?: number;
  cursor?: string | null;
}

export async function searchNotionPages(
  params: SearchNotionPagesParams,
): Promise<NotionPickerResponse> {
  return apiClient.get(
    `/admin/integrations/notion/workspaces/${params.workspaceId}/search`,
    notionPickerResponseSchema,
    {
      query: {
        q: params.q ?? "",
        type: params.type ?? "all",
        page_size: params.pageSize ?? 25,
        cursor: params.cursor ?? undefined,
      },
    },
  );
}

/**
 * Friendly mapping for picker-search failures. Returns null when the
 * error is something the caller should render verbatim (or retry).
 */
export function describeNotionPickerError(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;
  if (error.status === 404) return "workspace no encontrado";
  if (error.status === 409) {
    if (error.message.includes("workspace_revoked")) {
      return "Notion rechazó nuestra credencial — reconecta el workspace";
    }
    return "el workspace no está conectado";
  }
  if (error.status === 502) return "Notion devolvió un error inesperado";
  if (error.status === 503) {
    if (error.message.includes("integration_token_key_missing")) {
      return "el servidor no tiene configurada la clave de integraciones";
    }
    return "Notion no respondió a tiempo, intenta de nuevo";
  }
  return null;
}

/**
 * Friendly mapping for the most common Notion-register failures so the
 * UI can show actionable messages without re-implementing the matcher
 * in each component.
 */
export function describeRegisterNotionError(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;
  if (error.status === 400) {
    if (error.message.includes("workspace_not_connected")) {
      return "El workspace seleccionado no está conectado";
    }
    if (error.message.includes("unsupported_target")) {
      return "Esa URL o ID no es una página o base de datos de Notion";
    }
    return "Datos inválidos";
  }
  if (error.status === 409) {
    return "Ya hay un registro en curso para esa página — espera a que termine";
  }
  if (error.status === 422) {
    // Body validation: name the field and its cap. The form clamps both
    // inputs, so reaching this means a caller bypassed the UI.
    const entry = firstValidationEntry(error.payload);
    if (entry?.type === "string_too_long") {
      if (entry.field === "metadata") {
        return `El contexto supera el máximo de ${NOTION_METADATA_MAX_CHARS} caracteres`;
      }
      if (entry.field === "name") {
        return `El nombre supera el máximo de ${NOTION_NAME_MAX_CHARS} caracteres`;
      }
    }
    return "Datos inválidos";
  }
  if (error.status === 502) return "Notion rechazó la credencial — revisa la conexión";
  if (error.status === 503) return "Notion no respondió a tiempo, intenta de nuevo";
  return null;
}

/**
 * Friendly mapping for the most common URL-register failures so the UI can
 * show actionable messages without re-implementing the matcher in each
 * component. Returns `null` when the error is something the caller should
 * surface verbatim.
 */
export function describeRegisterUrlError(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;
  const costScope = parseLlmCostLimitScope(error.payload);
  if (costScope) return llmCostLimitMessage(costScope);
  if (error.status === 409) {
    // `get_active_repository_scope` fails closed with this code when the
    // scoped repository was archived after the page loaded (the client-side
    // `readOnly` gate is driven by a non-refetching query, so this race is
    // reachable in normal use) — distinct from the backend's duplicate-URL
    // conflict handled below.
    if (apiErrorCode(error) === "repository_archived") {
      return "el repositorio de conocimiento seleccionado está archivado y ya no admite fuentes nuevas";
    }
    return "esa URL ya estaba registrada";
  }
  if (error.status === 415) return "tipo de contenido no soportado en URL";
  if (error.status === 413)
    return "el contenido excede el tope (50 MiB para texto/imagen, 200 MiB para audio/video)";
  if (error.status === 422) {
    if (error.message.includes("youtube_no_transcript"))
      return "ese video de YouTube no tiene subtítulos disponibles";
    if (error.message.includes("youtube_video_unavailable"))
      return "el video de YouTube no está disponible (privado, borrado o restringido)";
  }
  if (error.status === 400) return "URL inválida";
  return null;
}

// ---- Shortcut registration ----------------------------------------

export const shortcutStoryRegistrationResultSchema = z.object({
  item: sourceListItemSchema,
  duplicate: z.boolean(),
});
export type ShortcutStoryRegistrationResult = z.infer<typeof shortcutStoryRegistrationResultSchema>;

export const shortcutBulkSyncResultSchema = z.object({
  sync_id: z.guid(),
  audit_url: z.string(),
});
export type ShortcutBulkSyncResult = z.infer<typeof shortcutBulkSyncResultSchema>;

export const shortcutSyncAuditSchema = z.object({
  sync_id: z.guid(),
  workspace_id: z.guid(),
  range_from: z.string(),
  range_to: z.string(),
  trigger: z.enum(["manual", "cron"]),
  status: z.enum(["running", "succeeded", "failed"]),
  stories_fetched: z.number().int(),
  stories_upserted: z.number().int(),
  stories_skipped: z.number().int(),
  stories_failed: z.number().int().default(0),
  error: z.string().nullable(),
  started_at: z.string(),
  finished_at: z.string().nullable(),
});
export type ShortcutSyncAudit = z.infer<typeof shortcutSyncAuditSchema>;

export async function registerShortcutStory(payload: {
  workspace_id: string;
  public_id: number;
}): Promise<ShortcutStoryRegistrationResult> {
  return apiClient.post("/sources/shortcut/story", shortcutStoryRegistrationResultSchema, payload);
}

export async function startShortcutBulkSync(payload: {
  workspace_id: string;
  updated_from?: string;
  updated_to?: string;
}): Promise<ShortcutBulkSyncResult> {
  return apiClient.post("/sources/shortcut/bulk", shortcutBulkSyncResultSchema, payload);
}

export async function getShortcutSyncStatus(sync_id: string): Promise<ShortcutSyncAudit> {
  return apiClient.get("/sources/shortcut/sync-status", shortcutSyncAuditSchema, {
    query: { sync_id },
  });
}

// ---- Postman registration ----------------------------------------

export const postmanRegistrationResultSchema = z.object({
  collection_uid: z.string(),
  folder_path: z.string().nullable().default(null),
  queued: z.boolean(),
});
export type PostmanRegistrationResult = z.infer<typeof postmanRegistrationResultSchema>;

/**
 * Register a Postman collection (or, when `folderPath` is given, a single
 * folder within it) as a knowledge source. Omitting `folderPath` (or passing
 * `null`) registers the whole collection — unchanged from the original
 * behavior.
 */
export async function registerPostmanCollection(
  collectionUid: string,
  folderPath?: string | null,
): Promise<PostmanRegistrationResult> {
  return apiClient.post("/sources/postman", postmanRegistrationResultSchema, {
    collection_uid: collectionUid,
    folder_path: folderPath ?? null,
  });
}
